import { useGenerateDeepLink } from "@src/appV2/DeepLinks";
import { environmentConfig } from "@src/appV2/environment";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { useToast } from "@src/appV2/lib";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { openInBrowser } from "@src/appV2/NativeAdapters/browser.adapter";
import { useCallback } from "react";
import { useLocation } from "react-router-dom";

import { useGenerateStripeOnboardingLink } from "../api/useGenerateStripeOnboardingLink";

export function useGoToStripeOnboarding(): {
  goToStripeOnboarding: () => void;
  isGoToStripeOnboardingLoading: boolean;
} {
  const { showErrorToast } = useToast();
  const location = useLocation();
  const stripeReturnLinkForceRedirect = useCbhFlag(
    CbhFeatureFlag.STRIPE_RETURN_LINK_FORCE_REDIRECT,
    {
      defaultValue: false,
    }
  );

  const {
    mutateAsync: generateStripeOnboardingLink,
    isLoading: isGenerateStripeOnboardingLinkLoading,
  } = useGenerateStripeOnboardingLink();

  const { mutateAsync: generateDeepLink, isLoading: isGenerateDeepLinkLoading } =
    useGenerateDeepLink();

  const isGoToStripeOnboardingLoading =
    isGenerateDeepLinkLoading || isGenerateStripeOnboardingLinkLoading;

  const goToStripeOnboarding = useCallback(async () => {
    try {
      const returnUrl = await generateDeepLink({
        link: `${environmentConfig.REACT_APP_WEB_APP_URL}${location.pathname}`,
        skipPreview: stripeReturnLinkForceRedirect,
      });
      const { data: link } = await generateStripeOnboardingLink({
        returnUrl,
        refreshUrl: returnUrl,
      });

      await openInBrowser({ url: link });
    } catch (error) {
      showErrorToast("Failed to open Stripe, please try again!");
      logEvent(APP_V2_APP_EVENTS.GENERATE_STRIPE_ONBOARDING_LINK_FAILURE, { error });
    }
  }, [
    generateDeepLink,
    generateStripeOnboardingLink,
    showErrorToast,
    stripeReturnLinkForceRedirect,
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- Intentionally not including location.pathname
  ]);

  return { goToStripeOnboarding, isGoToStripeOnboardingLoading };
}
